import React, { type ReactElement } from 'react'

import { createBrowserRouter, Outlet } from 'react-router-dom'

import { useDirectoryLocaleSync } from '../hooks/useDirectoryLocaleSync'
import { BrActivationRoute } from './BrActivationRoute'
import { CareAuthenticationRequestRoute } from './CareAuthenticationRequestRoute'
import { ChangeEmailRoute } from './ChangeEmailRoute'
import { ChangePasswordAndroidRoute } from './ChangePasswordAndroidRoute'
import { ChangePasswordIOSRoute } from './ChangePasswordIOSRoute'
import { ChangePasswordRoute } from './ChangePasswordRoute'
import { ChangePhoneNumberRoute } from './ChangePhoneNumberRoute'
import { ChangeProfileEmailRoute } from './ChangeProfileEmailRoute'
import { ConnectionsManagementAndroidRoute } from './ConnectionsManagementAndroidRoute'
import { ConnectionsManagementIOSRoute } from './ConnectionsManagementIOSRoute'
import { ConnectionsManagementRoute } from './ConnectionsManagementRoute'
import { ForgotPasswordRoute } from './ForgotPasswordRoute'
import { IdentityVerificationReturnRoute } from './IdentityVerificationReturnRoute'
import { LoginSuccessRoute } from './LoginSuccessRoute'
import { PasskeyManagementAndroidRoute } from './PasskeyManagementAndroidRoute'
import { PasskeyManagementIOSRoute } from './PasskeyManagementIOSRoute'
import { PasskeyManagementRoute } from './PasskeyManagementRoute'
import { ProfileAndroidRoute } from './ProfileAndroidRoute'
import { ProfileIOSRoute } from './ProfileIOSRoute'
import { ProfileRoute } from './ProfileRoute'
import { ResetPasswordRoute } from './ResetPasswordRoute'
import { SentryReactTestRoute } from './SentryReactTestRoute'
import { SignInAndroidRoute } from './SignInAndroidRoute'
import { SignInIOSRoute } from './SignInIOSRoute'
import { SignInMobileRoute } from './SignInMobileRoute'
import { SignInPartnerRoute } from './SignInPartnerRoute'
import { SignInRoute } from './SignInRoute'
import { TrustedDeviceManagementAndroidRoute } from './TrustedDeviceManagementAndroidRoute'
import { TrustedDeviceManagementIOSRoute } from './TrustedDeviceManagementIOSRoute'
import { TrustedDeviceManagementRoute } from './TrustedDeviceManagementRoute'
import { TwoStepVerificationPushResponseRoute } from './TwoStepVerificationPushResponseRoute'
import { TwoStepVerificationTesterRoute } from './TwoStepVerificationTesterRoute'
import { UpdateAppRoute } from './UpdateAppRoute'

/**
 * Root layout mounted around every route. It runs the Directory locale sync inside
 * the Router context so the hook can retry on navigation (useDirectoryLocaleSync),
 * and renders the matched route via <Outlet />.
 */
const RootLayout = (): ReactElement => {
  useDirectoryLocaleSync()
  return <Outlet />
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/signin',
        element: <SignInRoute />,
      },
      {
        path: '/signin/mobile',
        element: <SignInMobileRoute />,
      },
      {
        path: '/signin/ios',
        element: <SignInIOSRoute />,
      },
      {
        path: '/signin/android',
        element: <SignInAndroidRoute />,
      },
      {
        path: '/signin/activation/br',
        element: <BrActivationRoute />,
      },
      {
        path: '/update_app',
        element: <UpdateAppRoute />,
      },
      {
        path: '/forgot_password',
        element: <ForgotPasswordRoute />,
      },
      {
        path: '/reset_password',
        element: <ResetPasswordRoute />,
      },
      {
        path: '/care_authentication_request',
        element: <CareAuthenticationRequestRoute />,
      },
      {
        path: '/identity_verifications/return',
        element: <IdentityVerificationReturnRoute />,
      },
      {
        path: '/two_step_verification/push/response',
        element: <TwoStepVerificationPushResponseRoute />,
      },
      {
        path: '/two_step_verification_tester',
        element: <TwoStepVerificationTesterRoute />,
      },
      {
        path: '/signin_partner',
        element: <SignInPartnerRoute />,
      },
      {
        path: '/change_email',
        element: <ChangeEmailRoute />,
      },
      {
        path: '/profile/change_password',
        element: <ChangePasswordRoute />,
      },
      {
        path: '/profile/change_password/ios',
        element: <ChangePasswordIOSRoute />,
      },
      {
        path: '/profile/change_password/android',
        element: <ChangePasswordAndroidRoute />,
      },
      {
        path: '/profile/passkey_management',
        element: <PasskeyManagementRoute />,
      },
      {
        path: '/profile/passkey_management/ios',
        element: <PasskeyManagementIOSRoute />,
      },
      {
        path: '/profile/passkey_management/android',
        element: <PasskeyManagementAndroidRoute />,
      },
      {
        path: '/profile/trusted_device_management',
        element: <TrustedDeviceManagementRoute />,
      },
      {
        path: '/profile/trusted_device_management/ios',
        element: <TrustedDeviceManagementIOSRoute />,
      },
      {
        path: '/profile/trusted_device_management/android',
        element: <TrustedDeviceManagementAndroidRoute />,
      },
      {
        path: '/profile/connections_management',
        element: <ConnectionsManagementRoute />,
      },
      {
        path: '/profile/connections_management/ios',
        element: <ConnectionsManagementIOSRoute />,
      },
      {
        path: '/profile/connections_management/android',
        element: <ConnectionsManagementAndroidRoute />,
      },
      {
        path: '/admin/debug/sentry_react_test',
        element: <SentryReactTestRoute />,
      },
      {
        path: '/login_success',
        element: <LoginSuccessRoute />,
      },
      {
        path: '/profile',
        element: <ProfileRoute />,
      },
      {
        path: '/profile/ios',
        element: <ProfileIOSRoute />,
      },
      {
        path: '/profile/android',
        element: <ProfileAndroidRoute />,
      },
      {
        path: '/profile/change_phone_number',
        element: <ChangePhoneNumberRoute />,
      },
      {
        path: '/profile/change_email',
        element: <ChangeProfileEmailRoute />,
      },
    ],
  },
])
